import { MailPlus, RotateCw, Trash2, UserMinus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';
import { StatusBadge } from '@/components/status-badge';
import { Page } from '@/components/shell/page';
import { DataTable } from '@/components/data-table/data-table';
import { columnsFor } from '@/components/data-table/features';
import { EmptyState } from '@/components/empty-state';
import { QueryState } from '@/components/query-state';
import { LoadingState } from '@/components/states';
import { Field } from '@/components/form/field';
import { FormError } from '@/components/form/form-error';
import { SelectField } from '@/components/form/select-field';
import { SelectInput } from '@/components/form/select-input';
import { optionsFromLabels } from '@/components/form/select-options';
import { SwitchField } from '@/components/form/switch-field';
import { useZodForm } from '@/components/form/use-zod-form';
import { ConfirmDialog } from '@/components/modal/confirm-dialog';
import { Modal } from '@/components/modal/modal';
import { ModalActions } from '@/components/modal/modal-actions';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import type { MembershipRole } from '@/features/auth/api/auth';
import { useAuth } from '@/features/auth/use-auth';
import { requiredEmail } from '@/lib/schemas';
import { toast, toastApiError } from '@/lib/toast';
import {
  type Member,
  ROLE_LABELS,
  useInvitations,
  useInvite,
  useMembers,
  useRemoveMember,
  useResendInvitation,
  useRevokeInvitation,
  useUpdateMember,
} from '../api/organization';

const ROLE_OPTIONS = optionsFromLabels<MembershipRole>(ROLE_LABELS);
/** Papéis que já trazem custo e margem (ADR-052): a chave por membro não se aplica a eles. */
const SEES_COSTS = new Set<MembershipRole>(['ADMIN', 'MANAGER']);

export function MembersPage() {
  const { user } = useAuth();
  const members = useMembers();
  const invitations = useInvitations();
  const removeMember = useRemoveMember();
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);

  const { mutateAsync: updateMemberAsync } = useUpdateMember();
  const update = useCallback(
    (member: Member, changes: MemberChanges) =>
      updateMemberAsync({
        id: member.id,
        role: member.role,
        canSeeFinancials: member.canSeeFinancials,
        ...changes,
      }).catch((error: unknown) => toastApiError(error, 'Não foi possível alterar o membro.')),
    [updateMemberAsync],
  );

  return (
    <Page>
      <PageHeader
        title="Membros da empresa"
        description="Quem acessa esta empresa, com que papel e se vê custos e lucro."
        actions={
          <Button onClick={() => setInviting(true)}>
            <MailPlus aria-hidden /> Convidar
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Membros</CardTitle>
        </CardHeader>
        <CardContent>
          <QueryState query={members} loading={<LoadingState rows={2} />}>
            {(data) => <MembersTable members={data} onUpdate={update} onRemove={setRemoving} />}
          </QueryState>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Convites pendentes</CardTitle>
        </CardHeader>
        <CardContent>
          <QueryState
            query={invitations}
            loading={<LoadingState rows={1} />}
            isEmpty={(data) => data.length === 0}
            empty={
              <EmptyState
                title="Nenhum convite pendente"
                description="Convites aceitos viram membros."
              />
            }
          >
            {(data) => (
              <ul className="divide-y">
                {data.map((invitation) => (
                  <li
                    key={invitation.id}
                    className="flex items-center justify-between gap-4 py-2 text-sm"
                  >
                    <span>
                      <span className="font-medium">{invitation.email}</span>{' '}
                      <span className="text-muted-foreground">
                        · {ROLE_LABELS[invitation.role]} · vence em{' '}
                        {new Date(invitation.expiresAt).toLocaleDateString('pt-BR')}
                      </span>
                    </span>
                    <span className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Reenviar convite para ${invitation.email}`}
                        onClick={() =>
                          resend
                            .mutateAsync(invitation.id)
                            .then(() => toast.success('Convite reenviado com link novo.'))
                            .catch((error: unknown) => toastApiError(error))
                        }
                      >
                        <RotateCw aria-hidden />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Revogar convite para ${invitation.email}`}
                        onClick={() =>
                          revoke
                            .mutateAsync(invitation.id)
                            .catch((error: unknown) => toastApiError(error))
                        }
                      >
                        <Trash2 aria-hidden />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </QueryState>
        </CardContent>
      </Card>

      <InviteModal open={inviting} onOpenChange={setInviting} />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Remover da empresa"
        description={
          removing
            ? `${removing.name} perde o acesso a esta empresa na hora${removing.userId === user?.id ? ', inclusive você' : ''}. A conta continua existindo e o histórico é preservado.`
            : ''
        }
        confirmLabel="Remover"
        isLoading={removeMember.isPending}
        onConfirm={() => {
          if (!removing) return;
          removeMember
            .mutateAsync(removing.id)
            .then(() => setRemoving(null))
            .catch((error: unknown) => toastApiError(error, 'Não foi possível remover.'));
        }}
      />
    </Page>
  );
}

type MemberChanges = Partial<Pick<Member, 'role' | 'canSeeFinancials'>>;

const memberColumn = columnsFor<Member>();

/**
 * Membros da empresa. No celular ficam pessoa, papel e a ação; financeiro e 2FA aparecem a
 * partir do tablet.
 */
function MembersTable({
  members,
  onUpdate,
  onRemove,
}: {
  members: Member[];
  onUpdate: (member: Member, changes: MemberChanges) => void;
  onRemove: (member: Member) => void;
}) {
  const columns = useMemo(
    () =>
      memberColumn.columns([
        memberColumn.accessor('name', {
          header: 'Pessoa',
          enableSorting: true,
          sortFn: 'text',
          cell: ({ row }) => (
            <>
              <div className="font-medium">{row.original.name}</div>
              <div className="text-xs text-muted-foreground">{row.original.email}</div>
            </>
          ),
        }),
        memberColumn.accessor((member) => ROLE_LABELS[member.role], {
          id: 'role',
          header: 'Papel',
          sortFn: 'text',
          cell: ({ row }) => (
            <SelectInput
              aria-label={`Papel de ${row.original.name}`}
              className="w-40"
              value={row.original.role}
              options={ROLE_OPTIONS}
              onValueChange={(role) => role && onUpdate(row.original, { role })}
            />
          ),
        }),
        memberColumn.accessor('canSeeFinancials', {
          header: 'Vê custo e margem',
          meta: { hideBelow: 'md' },
          // Administrador e Gestor sempre veem: o controle fica travado para não sugerir o contrário.
          cell: ({ row }) => (
            <Switch
              aria-label={`${row.original.name} vê custo e margem`}
              checked={SEES_COSTS.has(row.original.role) || row.original.canSeeFinancials}
              disabled={SEES_COSTS.has(row.original.role)}
              onCheckedChange={(checked) => onUpdate(row.original, { canSeeFinancials: checked })}
            />
          ),
        }),
        memberColumn.accessor('twoFactorEnabled', {
          header: '2FA',
          meta: { hideBelow: 'md' },
          cell: ({ getValue }) =>
            getValue() ? (
              <StatusBadge tone="success">ativo</StatusBadge>
            ) : (
              <StatusBadge tone="neutral">inativo</StatusBadge>
            ),
        }),
        memberColumn.display({
          id: 'actions',
          header: () => <span className="sr-only">Ações</span>,
          meta: { align: 'right' },
          cell: ({ row }) => (
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Remover ${row.original.name} da empresa`}
              onClick={() => onRemove(row.original)}
            >
              <UserMinus aria-hidden />
            </Button>
          ),
        }),
      ]),
    [onUpdate, onRemove],
  );
  return <DataTable data={members} columns={columns} getRowId={(item) => item.id} />;
}

const inviteSchema = z.object({
  email: requiredEmail,
  role: z.enum(Object.keys(ROLE_LABELS) as [MembershipRole, ...MembershipRole[]]),
  canSeeFinancials: z.boolean(),
});

function InviteModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invite = useInvite();
  const form = useZodForm(inviteSchema, {
    email: '',
    role: 'OPERATOR',
    canSeeFinancials: false,
  });
  const role = form.watch('role');

  function close() {
    form.reset();
    invite.reset();
    onOpenChange(false);
  }

  const submit = form.handleSubmit(async (values) => {
    const sent = await invite
      .mutateAsync(values)
      .then(() => true)
      .catch(() => false);
    if (!sent) return;
    toast.success(`Convite enviado para ${values.email}.`);
    close();
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Convidar para a empresa"
      description="A pessoa recebe um link por e-mail, válido por 7 dias."
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <FormError error={invite.error} fallback="Não foi possível enviar o convite." />
        <Field control={form.control} name="email" label="E-mail" required>
          {(field) => <Input {...field} type="email" />}
        </Field>
        <SelectField
          control={form.control}
          name="role"
          label="Papel"
          required
          options={ROLE_OPTIONS}
        />
        {!SEES_COSTS.has(role) && (
          <SwitchField
            control={form.control}
            name="canSeeFinancials"
            label="Vê custo e margem"
            description="Custo, lucro e margem dos produtos e pedidos. Faturamento da empresa e o módulo Financeiro ficam de fora."
          />
        )}
        <div className="flex justify-end gap-2">
          <ModalActions
            onCancel={close}
            confirmType="submit"
            confirmLabel="Enviar convite"
            isLoading={invite.isPending}
          />
        </div>
      </form>
    </Modal>
  );
}
